import { useMemo, useState } from 'react';
import { App, Typography, Radio, Spin } from 'antd';
import type { Tenant } from '../api/types';
import { manuallyLock, unlock, resetAdmin, triggerBackup, schedulePurge, listBackups, restoreBackup, type BackupFile } from '../api/ops';

interface Props {
  tenant: Tenant;
  onRefresh: () => void;
}

interface DangerOp {
  key: string;
  text: string;
  hint: string;
  btnLabel: string;
  destructive?: boolean;
  /** 若非空：按钮 disabled 并把原因附加到 hint 行 */
  disabledReason?: string;
  /** 自定义触发逻辑（默认走 confirm + run + toast） */
  trigger: () => void;
}

export function DangerZone({ tenant, onRefresh }: Props) {
  const [code, setCode] = useState('');
  const { message, modal } = App.useApp();

  const unlocked = code === tenant.code;
  const isLocked = tenant.status === 'manually_locked';
  const isTerminated = tenant.status === 'terminated';
  const isProvisioning = tenant.status === 'provisioning';

  function showOneTimePassword(pwd: string) {
    modal.success({
      title: '新密码（仅显示一次）',
      width: 480,
      okText: '我已记下，关闭',
      content: (
        <div>
          <div style={{ color: 'var(--c-destructive)', marginBottom: 12, fontSize: 13 }}>
            请立即复制并离线交付给客户。关闭弹窗后**无法再获取**，需要再次重置才能拿到新密码。
          </div>
          <Typography.Paragraph
            copyable={{ text: pwd, tooltips: ['复制', '已复制'] }}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 16,
              fontWeight: 700,
              background: 'var(--c-muted)',
              padding: '10px 14px',
              borderRadius: 6,
              margin: 0,
              wordBreak: 'break-all',
            }}
          >
            {pwd}
          </Typography.Paragraph>
        </div>
      ),
    });
  }

  function runSimple(label: string, content: string, destructive: boolean, fn: () => Promise<unknown>) {
    modal.confirm({
      title: label,
      content,
      okText: '确认',
      cancelText: '取消',
      okButtonProps: { danger: destructive },
      async onOk() {
        try {
          await fn();
          message.success('操作成功');
          onRefresh();
        } catch (e: unknown) {
          message.error(e instanceof Error ? e.message : '操作失败');
        }
      },
    });
  }

  function runReset() {
    modal.confirm({
      title: '重置租户 admin 密码',
      content: `将为租户 ${tenant.code} 生成一个一次性强密码（16 位），系统不再保存任何明文，仅作为本次重置的一次性返回。`,
      okText: '生成并重置',
      cancelText: '取消',
      async onOk() {
        try {
          const res = await resetAdmin(tenant.id, tenant.code);
          onRefresh();
          showOneTimePassword(res.newPassword);
        } catch (e: unknown) {
          message.error(e instanceof Error ? e.message : '重置失败');
        }
      },
    });
  }

  function runRestore() {
    let selected: string | undefined;
    let files: BackupFile[] = [];
    let loading = true;

    const m = modal.confirm({
      title: '恢复备份',
      width: 560,
      okText: '确认还原（覆盖当前数据）',
      okButtonProps: { danger: true, disabled: true },
      cancelText: '取消',
      content: <div><Spin /> 加载备份列表…</div>,
      async onOk() {
        if (!selected) { message.error('请选择一个备份文件'); throw new Error('no selection'); }
        try {
          await restoreBackup(tenant.id, tenant.code, selected);
          message.success('已入队还原任务，可在任务监控查看进度');
          onRefresh();
        } catch (e: unknown) {
          message.error(e instanceof Error ? e.message : '还原失败');
          throw e;
        }
      },
    });

    const render = () => {
      m.update({
        content: loading ? (
          <div><Spin /> 加载备份列表…</div>
        ) : files.length === 0 ? (
          <div className="text-muted">该租户暂无可用备份。</div>
        ) : (
          <div>
            <div style={{ color: 'var(--c-destructive)', marginBottom: 12, fontSize: 13 }}>
              还原会用所选备份<strong>覆盖当前数据库</strong>。系统会在还原前自动对当前数据做一次安全备份兜底。
            </div>
            <Radio.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflow: 'auto' }}
              onChange={(e) => { selected = e.target.value; m.update({ okButtonProps: { danger: true, disabled: false } }); }}
            >
              {files.map((f) => (
                <Radio key={f.key} value={f.key}>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{f.key}</span>
                  <span className="text-muted text-xs" style={{ marginLeft: 8 }}>
                    {(f.size / 1024).toFixed(1)} KB · {new Date(f.last_modified).toLocaleString()}
                  </span>
                </Radio>
              ))}
            </Radio.Group>
          </div>
        ),
      });
    };
    render();
    listBackups(tenant.id)
      .then((items) => { files = items; })
      .catch(() => { files = []; })
      .finally(() => { loading = false; render(); });
  }

  const ops: DangerOp[] = useMemo(() => [
    {
      key: 'lock',
      text: isLocked ? '解除手动冻结' : '手动冻结租户',
      hint: isLocked
        ? '将状态从 manually_locked 恢复到冻结前的 active/grace/read_only'
        : '立即停止读写，sweeper 跳过推进，可随时恢复',
      btnLabel: isLocked ? '解冻' : '冻结',
      disabledReason: isProvisioning ? '租户正在开通中，暂不可冻结' : undefined,
      trigger: () => runSimple(
        isLocked ? '解冻租户' : '冻结租户',
        isLocked ? `将立即解锁租户 ${tenant.code}。` : `将立即冻结租户 ${tenant.code}，所有用户无法访问。`,
        false,
        () => (isLocked ? unlock(tenant.id, tenant.code) : manuallyLock(tenant.id, tenant.code, '管理员手动冻结')),
      ),
    },
    {
      key: 'reset',
      text: '重置租户 admin 密码',
      hint: '写入新 password_hash 到租户库 users 表；明文仅本次弹窗一次性显示',
      btnLabel: '重置…',
      disabledReason: !['active', 'grace', 'read_only'].includes(tenant.status)
        ? `仅 active / grace / read_only 状态可重置（当前: ${tenant.status}）`
        : undefined,
      trigger: runReset,
    },
    {
      key: 'backup',
      text: '立即触发完整备份',
      hint: '入队 backup job · pg_dump custom · 上传 owlsome-backups',
      btnLabel: '备份',
      disabledReason: isProvisioning ? '租户正在开通中，暂不可备份' : undefined,
      trigger: () => runSimple(
        '触发备份',
        `确认为租户 ${tenant.code} 入队一次完整备份？`,
        false,
        () => triggerBackup(tenant.id, tenant.code),
      ),
    },
    {
      key: 'restore',
      text: '恢复备份',
      hint: '从 owlsome-backups 选择备份还原 · 还原前自动安全备份 + 覆盖当前库',
      btnLabel: '恢复…',
      destructive: true,
      disabledReason: !['active', 'grace', 'read_only', 'suspended', 'manually_locked'].includes(tenant.status)
        ? `当前状态(${tenant.status})不可还原`
        : undefined,
      trigger: runRestore,
    },
    {
      key: 'purge',
      text: '提前调度清退（terminate）',
      hint: '入队 purge job · 仍走四重校验后才会 DROP DATABASE',
      btnLabel: '调度清退…',
      destructive: true,
      disabledReason: tenant.status !== 'suspended'
        ? `仅 suspended 状态可调度清退（当前: ${tenant.status}）`
        : undefined,
      trigger: () => runSimple(
        '调度清退',
        `此操作不可逆！将入队 purge job，租户 ${tenant.code} 的数据库将被 DROP DATABASE 永久清除。`,
        true,
        () => schedulePurge(tenant.id, tenant.code),
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [tenant.code, tenant.id, tenant.status, isLocked]);

  // 终态：terminated 的租户库已被 DROP，所有写操作不再可用
  if (isTerminated) {
    return (
      <div className="alert alert--danger" style={{ marginBottom: 24 }}>
        <div>
          <div className="text-strong mb-2">⚠ 租户已清退（terminated）</div>
          <div className="text-xs">
            数据库 <span className="text-mono">{tenant.db_name}</span> 已被 DROP，
            所有写操作（备份 / 冻结 / 解冻 / 重置 admin / 调度清退 / 续签合同）均不可用。
            如需恢复，请走人工 <span className="text-mono">pg_restore</span> 流程，
            并由平台 OPS 在 meta_db 中调整 <span className="text-mono">tenants.status</span>。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="alert alert--danger mb-6" style={{ marginBottom: 24 }}>
        <div>
          <div className="text-strong mb-2">⚠ 高危区域</div>
          <div className="text-xs">
            下列操作均会写入 <span className="text-mono">tenant_lifecycle_events</span>。
            涉及 DROP DATABASE / 强制停服 的操作必须通过四重校验。
            <strong>请输入租户 code 解锁按钮，避免误触。</strong>
          </div>
        </div>
      </div>

      <div className="danger-unlock">
        <div className="field">
          <label className="field__label">输入租户 code 解锁危险操作<span className="req">*</span></label>
          <input
            className="input"
            placeholder={`例如：${tenant.code}`}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
          />
          <div className="field__hint">必须与本租户 code 完全匹配（大小写敏感）</div>
        </div>
        <div className={`danger-unlock__status ${unlocked ? 'unlocked' : 'locked'}`}>
          {unlocked ? '🔓 已解锁，请谨慎操作' : '🔒 已锁定'}
        </div>
      </div>

      <div className="card danger-zone">
        <div className="card__body" style={{ padding: 0 }}>
          {ops.map((op) => {
            const disabled = !unlocked || !!op.disabledReason;
            return (
              <div key={op.key} className="danger-action">
                <div>
                  <div className="danger-action__text" style={op.destructive ? { color: 'var(--c-destructive)' } : undefined}>
                    {op.text}
                  </div>
                  <div className="danger-action__hint">
                    {op.hint}
                    {op.disabledReason && (
                      <span style={{ color: 'var(--c-warning)', marginLeft: 6 }}>· {op.disabledReason}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={op.destructive ? 'btn btn-danger btn-sm' : 'btn btn-secondary btn-sm'}
                  disabled={disabled}
                  onClick={op.trigger}
                  title={!unlocked ? '请先输入正确 code 解锁' : op.disabledReason}
                >
                  {op.btnLabel}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
